import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import DataTable from 'react-data-table-component';

import { perfilProveedor, limpiarProveedor } from '../../actions/proveedores';
import { editarBreadcrumb } from '../../actions/breadcrumb';
import { formatNumber } from '../../utils/generalFunctions';

const PerfilProveedor = () => {

    const dispatch = useDispatch();
    const { id } = useParams();

    const perfil = useSelector(state => state.proveedores.perfil);

    useEffect(() => {
        if (id) {
            dispatch(perfilProveedor(id));
            dispatch(editarBreadcrumb([
                { nombre: 'Proveedores', path: '/proveedores', activo: false },
                { nombre: 'Perfil del proveedor', path: `/proveedores/perfil/${id}`, activo: true }
            ]));
        }

        return () => {
            dispatch(limpiarProveedor());
        };
    }, [id]);

    const totalGastos = (perfil?.gastos || []).reduce((acc, g) => acc + (g.total || 0), 0);

    const columnsGastos = [
        {
            name: 'Sucursal',
            sortable: true,
            selector: row => row.sucursal?.nombre || '-'
        },
        {
            name: 'Método',
            sortable: true,
            selector: row => row.metodo?.nombre || '-'
        },
        {
            name: 'Fecha',
            sortable: true,
            selector: row => row.fecha
                ? new Date(row.fecha).toLocaleString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                : '-'
        },
        {
            name: 'Concepto',
            sortable: true,
            selector: row => row.concepto || '-',
            grow: 2
        },
        {
            name: 'Total',
            sortable: true,
            selector: row => row.total != null ? formatNumber(row.total, 2, '$', '') : '-'
        }
    ];

    return (
        <div className="row mt-5">

            {/* Header */}
            <div className="col-12 mb-4">
                <div className="d-flex align-items-center gap-3">
                    <div className="rounded-circle bg-secondary bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: 48, height: 48, minWidth: 48 }}>
                        <i className="fas fa-building text-primary fs-5"></i>
                    </div>
                    <div>
                        <h4 className="mb-0 fw-semibold">{perfil?.nombre || 'Perfil del proveedor'}</h4>
                        <small className="text-muted">Información completa del proveedor</small>
                    </div>
                </div>
            </div>

            {/* KPIs */}
            <div className="col-md-6 mb-3">
                <div className="card border-0 shadow-sm h-100">
                    <div className="card-body d-flex align-items-center gap-3">
                        <div className="rounded-circle bg-warning bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: 44, height: 44, minWidth: 44 }}>
                            <i className="fas fa-file-invoice-dollar text-warning"></i>
                        </div>
                        <div>
                            <div className="text-muted small">Total en gastos</div>
                            <div className="fw-bold fs-5">{formatNumber(totalGastos, 2, '$', '')}</div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-md-6 mb-3">
                <div className="card border-0 shadow-sm h-100">
                    <div className="card-body d-flex align-items-center gap-3">
                        <div className="rounded-circle bg-info bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: 44, height: 44, minWidth: 44 }}>
                            <i className="fas fa-list text-info"></i>
                        </div>
                        <div>
                            <div className="text-muted small">Gastos registrados</div>
                            <div className="fw-bold fs-5">{perfil?.gastos?.length || 0}</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Información del proveedor */}
            <div className="col-12 mb-3">
                <div className="card border-0 shadow-sm">
                    <div className="card-header bg-white border-bottom py-3">
                        <h6 className="mb-0 fw-semibold">
                            <i className="fas fa-info-circle text-primary me-2"></i>
                            Información del proveedor
                        </h6>
                    </div>
                    <div className="card-body">
                        <div className="row g-3">
                            <div className="col-md-6">
                                <div className="border-start border-primary border-3 ps-3">
                                    <div className="text-muted small mb-1">Nombre</div>
                                    <div className="fw-semibold">{perfil?.nombre || '-'}</div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="border-start border-primary border-3 ps-3">
                                    <div className="text-muted small mb-1">Correo</div>
                                    <div className="fw-semibold">{perfil?.correo || '-'}</div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="border-start border-secondary border-3 ps-3">
                                    <div className="text-muted small mb-1">Teléfono</div>
                                    <div className="fw-semibold">{perfil?.telefono || '-'}</div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="border-start border-secondary border-3 ps-3">
                                    <div className="text-muted small mb-1">Contacto</div>
                                    <div className="fw-semibold">{perfil?.contacto || '-'}</div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="border-start border-warning border-3 ps-3">
                                    <div className="text-muted small mb-1">RFC</div>
                                    <div className="fw-semibold">{perfil?.rfc || '-'}</div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="border-start border-warning border-3 ps-3">
                                    <div className="text-muted small mb-1">Dirección</div>
                                    <div className="fw-semibold">{perfil?.direccion || '-'}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Gastos del proveedor */}
            <div className="col-12">
                <div className="card border-0 shadow-sm">
                    <div className="card-header bg-white border-bottom py-3 d-flex align-items-center justify-content-between">
                        <h6 className="mb-0 fw-semibold">
                            <i className="fas fa-file-invoice text-warning me-2"></i>
                            Gastos del proveedor
                        </h6>
                        {perfil?.gastos?.length > 0 && (
                            <span className="badge bg-warning rounded-pill">{perfil.gastos.length}</span>
                        )}
                    </div>
                    <div className="card-body p-0">
                        <DataTable
                            columns={columnsGastos}
                            data={perfil?.gastos || []}
                            pagination
                            paginationPerPage={20}
                            paginationComponentOptions={{ rowsPerPageText: 'Filas por página', rangeSeparatorText: 'de' }}
                            highlightOnHover
                            noDataComponent={
                                <div className="py-5 text-center text-muted">
                                    <i className="fas fa-file-invoice fa-2x mb-2 d-block opacity-25"></i>
                                    No hay gastos registrados para este proveedor
                                </div>
                            }
                        />
                    </div>
                </div>
            </div>

        </div>
    );
};

export default PerfilProveedor;
